import { useEffect, useRef, useState } from "react"

export function useUserCache(client: UserClient) {
    const serviceRef = useRef<UserCacheService | null>(null)
    const [, forceUpdate] = useState({})

    if (!serviceRef.current) {
        serviceRef.current = new UserCacheService(client)
    }

    useEffect(() => {
        const service = serviceRef.current!

        const unsubscribe = service.subscribe(() => {
            forceUpdate({})
        })

        return unsubscribe
    }, [])

    return {
        getUserDisplayName: (userIdentifier: string, serviceAccount: string | undefined) => serviceRef.current!.getUserDisplayName(userIdentifier, serviceAccount),
        prefetchUsers: (userIdentifiers: string[]) => serviceRef.current!.prefetchUsers(userIdentifiers),
        isUserFetching: (userIdentifier: string) => serviceRef.current!.isUserFetching(userIdentifier),
        isUserCached: (userIdentifier: string) => serviceRef.current!.isUserCached(userIdentifier),
        getCachedUserName: (userIdentifier: string) => serviceRef.current!.getCachedUserName(userIdentifier),
        clearCache: () => serviceRef.current!.clearCache(),
        getCacheStats: () => serviceRef.current!.getCacheStats()
    }
}

export interface UserClient {
    users: {
        retrieve: (id: string) => Promise<{ name?: string } | null>
    }
}

export class UserCacheService {
    private userCache: Map<string, string> = new Map()
    private fetchingUsers: Set<string> = new Set()
    private listeners: Set<() => void> = new Set()
    private client: UserClient

    constructor(client: UserClient) {
        this.client = client
    }

    subscribe(listener: () => void): () => void {
        this.listeners.add(listener)
        return () => {
            this.listeners.delete(listener)
        }
    }

    private notifyListeners(): void {
        this.listeners.forEach(listener => listener())
    }

    getUserDisplayName(userIdentifier: string, serviceAccount: string | undefined): string {
        if (!userIdentifier) {
            return "Unknown User"
        }

        const [type, id] = userIdentifier.split(":")

        if (!id) {
            return "Unknown User"
        }

        if (type === "user") {
            if (this.userCache.has(userIdentifier)) {
                return this.userCache.get(userIdentifier)!
            }

            if (!this.fetchingUsers.has(userIdentifier)) {
                this.fetchUserName(userIdentifier)
            }

            return "Loading..."
        } else if (type === "system") {
            return "System"
        } else if (type === "service_account") {
            return serviceAccount ?? "Service Account"
        }

        return "Unknown User"
    }

    private async fetchUserName(userIdentifier: string): Promise<void> {
        const [type, id] = userIdentifier.split(":")

        if (type !== "user" || !id) {
            return
        }

        if (this.userCache.has(userIdentifier) || this.fetchingUsers.has(userIdentifier)) {
            return
        }

        this.fetchingUsers.add(userIdentifier)

        try {
            const user = await this.client.users.retrieve(id)
            const userName = user?.name || "Unknown User"

            this.userCache.set(userIdentifier, userName)

            this.notifyListeners()
        } catch (error) {
            this.userCache.set(userIdentifier, "Unknown User")
            this.notifyListeners()
        } finally {
            this.fetchingUsers.delete(userIdentifier)
        }
    }

    async prefetchUsers(userIdentifiers: string[]): Promise<void> {
        const uniqueUsers = new Set(userIdentifiers.filter(id => {
            const [type] = id.split(":")
            return type === "user" && !this.userCache.has(id) && !this.fetchingUsers.has(id)
        }))

        const fetchPromises = Array.from(uniqueUsers).map(userId => this.fetchUserName(userId))
        await Promise.allSettled(fetchPromises)
    }

    isUserFetching(userIdentifier: string): boolean {
        return this.fetchingUsers.has(userIdentifier)
    }

    isUserCached(userIdentifier: string): boolean {
        return this.userCache.has(userIdentifier)
    }

    getCachedUserName(userIdentifier: string): string | null {
        return this.userCache.get(userIdentifier) || null
    }

    clearCache(): void {
        this.userCache.clear()
        this.fetchingUsers.clear()
        this.notifyListeners()
    }

    getCacheStats(): {
        cachedUsers: number
        fetchingUsers: number
        totalRequests: number
    } {
        return {
            cachedUsers: this.userCache.size,
            fetchingUsers: this.fetchingUsers.size,
            totalRequests: this.userCache.size + this.fetchingUsers.size
        }
    }
}